import {
  CheckCircle2,
  CircleAlert,
  Database,
  History,
  LoaderCircle,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import type {
  ReasonAdminTask,
  ReasonIngestionResult,
  ReasonSegmentContractV2Options,
  ReasonSegmentContractV2Result,
  ReasonSourceDiagnostic,
  ReasonStats,
  ReasonSubmissionHistoryEntry,
} from '../types';
import { getReasonTaskViewStatus } from '../types';

interface ReasonAdminProps {
  tasks: ReasonAdminTask[];
  stats?: ReasonStats | null;
  loading: boolean;
  loadingStats?: boolean;
  hasMore?: boolean;
  nowMs?: number;
  onRefresh: () => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  submissions?: Record<string, ReasonSubmissionHistoryEntry[]>;
  onLoadSubmissions?: (episodeId: string) => void;
  onReassign: (episodeId: string, operator: string) => Promise<void>;
  onRetrySync: (episodeId: string) => void;
  onRefreshSource: (episodeId: string) => void;
  onDiagnoseSource: (episodeId: string) => Promise<ReasonSourceDiagnostic>;
  ingestionRunning: boolean;
  ingestionResult: ReasonIngestionResult | null;
  onRunIngestion: (dryRun: boolean, maxPages: number, fromBeginning?: boolean) => void;
  segmentMigrationRunning?: boolean;
  segmentMigrationResult?: ReasonSegmentContractV2Result | null;
  onMigrateSegmentContract?: (
    dryRun: boolean,
    limit: number,
    options?: ReasonSegmentContractV2Options
  ) => void;
}

export function ReasonAdmin({
  tasks,
  stats = null,
  loading,
  loadingStats = false,
  hasMore = false,
  nowMs = Date.now(),
  onRefresh,
  onSearch = () => undefined,
  onLoadMore,
  submissions = {},
  onLoadSubmissions = () => undefined,
  onReassign,
  onRetrySync,
  onRefreshSource,
  onDiagnoseSource,
  ingestionRunning,
  ingestionResult,
  onRunIngestion,
  segmentMigrationRunning = false,
  segmentMigrationResult = null,
  onMigrateSegmentContract = () => undefined,
}: ReasonAdminProps) {
  const [query, setQuery] = useState('');
  const [operators, setOperators] = useState<Record<string, string>>({});
  const [reassigningEpisode, setReassigningEpisode] = useState<string | null>(null);
  const [reassignFeedback, setReassignFeedback] = useState<Record<string, 'success' | 'failed'>>(
    {}
  );
  const [maxPages, setMaxPages] = useState(1);
  const [diagnosingEpisode, setDiagnosingEpisode] = useState<string | null>(null);
  const [diagnostic, setDiagnostic] = useState<ReasonSourceDiagnostic | null>(null);
  const [historyEpisode, setHistoryEpisode] = useState<string | null>(null);
  const handleReassign = async (episodeId: string) => {
    const operator = (operators[episodeId] ?? '').trim();
    if (!operator || reassigningEpisode) return;
    setReassignFeedback((current) => {
      const next = { ...current };
      delete next[episodeId];
      return next;
    });
    setReassigningEpisode(episodeId);
    try {
      await onReassign(episodeId, operator);
      setOperators((current) => ({ ...current, [episodeId]: '' }));
      setReassignFeedback((current) => ({ ...current, [episodeId]: 'success' }));
    } catch {
      setReassignFeedback((current) => ({ ...current, [episodeId]: 'failed' }));
    } finally {
      setReassigningEpisode(null);
    }
  };
  const handleDiagnose = async (episodeId: string) => {
    if (diagnosingEpisode) return;
    setDiagnosingEpisode(episodeId);
    setDiagnostic(null);
    try {
      setDiagnostic(await onDiagnoseSource(episodeId));
    } catch {
      setDiagnostic(null);
    } finally {
      setDiagnosingEpisode(null);
    }
  };

  return (
    <main className="mx-auto w-full max-w-[1500px] px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-text-primary">Reason Admin</h1>
          <p className="mt-1 text-sm text-brand-text-secondary">{tasks.length} tasks</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <section
        aria-label="Queue summary"
        className="mt-6 grid border-y border-brand-border sm:grid-cols-2 lg:grid-cols-6"
      >
        {[
          ['Claimable pending', stats?.claimable_pending_tasks],
          ['Blocked pending', stats?.blocked_pending_tasks],
          ['Assigned drafts', stats?.assigned_drafts],
          ['Completed editable', stats?.editable_tasks],
          ['Completed read-only', stats?.locked_tasks],
        ].map(([label, value]) => (
          <div key={label} className="border-b border-brand-border px-4 py-3 lg:border-b-0">
            <div className="text-xs uppercase text-brand-text-secondary">{label}</div>
            <div className="mt-1 text-xl font-semibold text-brand-text-primary">
              {loadingStats ? '...' : Number(value ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
        <div className="px-4 py-3">
          <div className="text-xs uppercase text-brand-text-secondary">Last source ingestion</div>
          <div className="mt-1 text-sm font-medium text-brand-text-primary">
            {loadingStats
              ? 'Loading'
              : stats?.last_source_ingestion_at_ms == null
                ? 'Not recorded'
                : new Date(stats.last_source_ingestion_at_ms).toLocaleString()}
          </div>
          {stats?.last_ingestion_error ? (
            <p className="mt-1 truncate text-xs text-red-300" title={stats.last_ingestion_error}>
              {stats.last_ingestion_error}
            </p>
          ) : (
            <p className="mt-1 text-xs text-emerald-300">No recorded error</p>
          )}
        </div>
      </section>

      <section className="mt-6 border-y border-brand-border py-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1 text-xs text-brand-text-secondary">
            Max pages
            <input
              type="number"
              min={1}
              max={20}
              value={maxPages}
              onChange={(event) =>
                setMaxPages(Math.min(20, Math.max(1, Number(event.target.value) || 1)))
              }
              className="h-9 w-24 rounded-md border border-brand-border bg-brand-surface px-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
            />
          </label>
          <button
            type="button"
            disabled={ingestionRunning}
            onClick={() => onRunIngestion(true, maxPages)}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
          >
            <ScanSearch className="h-4 w-4" />
            Dry run
          </button>
          <button
            type="button"
            disabled={ingestionRunning}
            onClick={() => {
              if (window.confirm(`Import SecCapReason tasks from ${maxPages} source page(s)?`)) {
                onRunIngestion(false, maxPages);
              }
            }}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-orange px-3 text-sm font-medium text-white disabled:opacity-50"
          >
            <Database className="h-4 w-4" />
            Import tasks
          </button>
          <button
            type="button"
            disabled={ingestionRunning}
            onClick={() => onRunIngestion(false, 1, true)}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            Reconcile first page
          </button>
        </div>
        {ingestionResult ? (
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4 lg:grid-cols-6">
            <span>Candidates {ingestionResult.candidates}</span>
            <span>Ingestible {ingestionResult.ingestible}</span>
            <span>Not sampled {ingestionResult.not_sampled}</span>
            <span>Zero fail {ingestionResult.zero_fail}</span>
            <span>Invalid {ingestionResult.invalid_source}</span>
            <span>DDB checked {ingestionResult.ddb_reconciled}</span>
            <span>Source drift {ingestionResult.ddb_source_drift}</span>
            <span>Reconcile failed {ingestionResult.ddb_reconciliation_failed}</span>
            <span>Inserted {ingestionResult.inserted}</span>
            <span>Refreshed {ingestionResult.refreshed}</span>
            <span>Unchanged {ingestionResult.unchanged}</span>
            <span>Source changed {ingestionResult.source_changed}</span>
            <span>Quarantined {ingestionResult.quarantined}</span>
            <span>Pages {ingestionResult.pages ?? 0}</span>
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-brand-border pt-4">
          <span className="mr-2 text-xs uppercase text-brand-text-secondary">
            Segment contract V2
          </span>
          <button
            type="button"
            disabled={segmentMigrationRunning}
            onClick={() => onMigrateSegmentContract(true, 500)}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
          >
            <ScanSearch className="h-4 w-4" />
            Dry run
          </button>
          <button
            type="button"
            disabled={segmentMigrationRunning}
            onClick={() => {
              if (
                window.confirm(
                  'Rebuild up to 500 unassigned legacy tasks? Completed, partial, and assigned tasks remain unchanged.'
                )
              ) {
                onMigrateSegmentContract(false, 500);
              }
            }}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-brand-border px-3 text-sm text-brand-text-primary disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${segmentMigrationRunning ? 'animate-spin' : ''}`} />
            Rebuild unassigned
          </button>
        </div>
        {segmentMigrationResult ? (
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <span>Inspected {segmentMigrationResult.inspected}</span>
            <span>Unassigned {segmentMigrationResult.unassigned_rebuildable}</span>
            <span>Assigned empty {segmentMigrationResult.assigned_empty_rebuildable}</span>
            <span>Partial legacy {segmentMigrationResult.partial_legacy}</span>
            <span>Completed unchanged {segmentMigrationResult.completed_unchanged}</span>
            <span>Rebuilt {segmentMigrationResult.rebuilt}</span>
            <span>
              Zero-duration removed {segmentMigrationResult.zero_duration_segments_removed}
            </span>
            <span>Zero-fail blocked {segmentMigrationResult.zero_fail_blocked}</span>
            <span>Remaining legacy {segmentMigrationResult.remaining_legacy}</span>
          </div>
        ) : null}
      </section>

      <form
        className="relative mt-6 flex max-w-xl gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSearch(query);
        }}
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-text-secondary" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Exact Episode or operator"
          aria-label="Search tasks"
          className="h-10 min-w-0 flex-1 rounded-md border border-brand-border bg-brand-surface pl-9 pr-3 text-sm text-brand-text-primary outline-none focus:border-brand-orange"
        />
        <button
          type="submit"
          disabled={loading}
          className="h-10 rounded-md border border-brand-border px-4 text-sm text-brand-text-primary disabled:opacity-50"
        >
          Search
        </button>
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              onSearch('');
            }}
            className="h-10 rounded-md border border-brand-border px-3 text-sm text-brand-text-secondary"
          >
            Clear
          </button>
        ) : null}
      </form>

      <div className="mt-4 max-w-full overflow-x-auto rounded-lg border border-brand-border bg-brand-surface">
        <table className="w-full min-w-[1040px] text-left text-sm">
          <thead className="border-b border-brand-border bg-brand-surface-highlight/50 text-xs uppercase text-brand-text-secondary">
            <tr>
              <th className="px-4 py-3 font-medium">Episode</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">State</th>
              <th className="px-4 py-3 font-medium">Taxonomy</th>
              <th className="px-4 py-3 font-medium">Progress</th>
              <th className="px-4 py-3 font-medium">Sync</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => {
              const committed = task.first_committed_at_ms !== null;
              return (
                <tr key={task.episode_id} className="border-b border-brand-border/70 last:border-0">
                  <td className="px-4 py-3 font-mono text-xs text-brand-text-primary">
                    {task.episode_id}
                  </td>
                  <td className="px-4 py-3 text-brand-text-secondary">
                    {task.assigned_operator ?? 'Unassigned'}
                  </td>
                  <td className="px-4 py-3 capitalize text-brand-text-secondary">
                    {getReasonTaskViewStatus(task, nowMs).replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-brand-text-secondary">
                    {task.reason_taxonomy_status === 'v1_legacy'
                      ? 'V1 legacy'
                      : task.reason_taxonomy_status === 'v2'
                        ? 'V2'
                        : task.reason_taxonomy_status === 'mixed'
                          ? 'Mixed'
                          : 'Unlabeled'}
                  </td>
                  <td className="px-4 py-3 text-brand-text-secondary">
                    {task.completed_segment_count} / {task.fail_segment_count}
                  </td>
                  <td className="px-4 py-3 text-brand-text-secondary">
                    <span className="capitalize">{task.ddb_sync_status}</span>
                    {task.source_changed && (
                      <span className="ml-2 text-xs text-red-300">Source changed</span>
                    )}
                    {task.ddb_sync_error && (
                      <p
                        className="mt-1 max-w-64 truncate text-xs text-red-300"
                        title={task.ddb_sync_error}
                      >
                        {task.ddb_sync_error}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title="View submission history"
                        aria-label={`View submission history for ${task.episode_id}`}
                        disabled={!committed}
                        onClick={() => {
                          setHistoryEpisode(task.episode_id);
                          onLoadSubmissions(task.episode_id);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <History className="h-3.5 w-3.5" />
                      </button>
                      <input
                        value={operators[task.episode_id] ?? ''}
                        onChange={(event) => {
                          setOperators((current) => ({
                            ...current,
                            [task.episode_id]: event.target.value,
                          }));
                          setReassignFeedback((current) => {
                            const next = { ...current };
                            delete next[task.episode_id];
                            return next;
                          });
                        }}
                        disabled={committed}
                        aria-label={`New operator for ${task.episode_id}`}
                        className="h-8 w-44 rounded-md border border-brand-border bg-brand-dark px-2 text-xs text-brand-text-primary outline-none disabled:opacity-40"
                      />
                      <button
                        type="button"
                        disabled={
                          committed ||
                          Boolean(reassigningEpisode) ||
                          !(operators[task.episode_id] ?? '').trim()
                        }
                        onClick={() => void handleReassign(task.episode_id)}
                        className="inline-flex h-8 min-w-20 items-center justify-center gap-1.5 rounded-md border border-brand-border px-3 text-xs text-brand-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {reassigningEpisode === task.episode_id ? (
                          <>
                            <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                            Reassigning...
                          </>
                        ) : (
                          'Reassign'
                        )}
                      </button>
                      {reassignFeedback[task.episode_id] === 'success' ? (
                        <span
                          role="status"
                          aria-live="polite"
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-emerald-300"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Reassigned
                        </span>
                      ) : null}
                      {reassignFeedback[task.episode_id] === 'failed' ? (
                        <span
                          role="alert"
                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-red-300"
                        >
                          <CircleAlert className="h-3.5 w-3.5" />
                          Reassign failed
                        </span>
                      ) : null}
                      <button
                        type="button"
                        title="Retry DynamoDB sync"
                        aria-label={`Retry sync for ${task.episode_id}`}
                        disabled={
                          !committed ||
                          task.source_changed === true ||
                          (task.ddb_sync_status !== 'failed' && task.ddb_sync_status !== 'pending')
                        }
                        onClick={() => onRetrySync(task.episode_id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                      {task.source_changed ? (
                        <>
                          <button
                            type="button"
                            title="Compare task with current DDB source"
                            aria-label={`Diagnose source for ${task.episode_id}`}
                            disabled={Boolean(diagnosingEpisode)}
                            onClick={() => void handleDiagnose(task.episode_id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-primary disabled:opacity-40"
                          >
                            {diagnosingEpisode === task.episode_id ? (
                              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <ScanSearch className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Refresh source audit"
                            aria-label={`Refresh source audit for ${task.episode_id}`}
                            onClick={() => onRefreshSource(task.episode_id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-brand-border text-brand-text-primary"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : null}
                      {!committed &&
                      task.completed_segment_count > 0 &&
                      (task.segment_contract_version ?? 1) < 2 ? (
                        <button
                          type="button"
                          disabled={segmentMigrationRunning}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Repair legacy Segment contract for ${task.episode_id} at revision ${task.revision}? Existing reasons will be preserved; the repair is rejected if a labeled Segment would be removed.`
                              )
                            ) {
                              onMigrateSegmentContract(false, 1, {
                                include_partial_legacy: true,
                                episode_id: task.episode_id,
                                expected_revision: task.revision,
                              });
                            }
                          }}
                          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-brand-orange px-3 text-xs text-brand-orange disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <RefreshCw
                            className={`h-3.5 w-3.5 ${
                              segmentMigrationRunning ? 'animate-spin' : ''
                            }`}
                          />
                          Repair legacy draft
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {historyEpisode ? (
        <section
          aria-label={`Submission history for ${historyEpisode}`}
          className="mt-4 border border-brand-border bg-brand-surface px-4 py-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">Submission history</h2>
              <p className="mt-1 font-mono text-xs text-brand-text-secondary">{historyEpisode}</p>
            </div>
            <button
              type="button"
              onClick={() => setHistoryEpisode(null)}
              className="h-8 rounded-md border border-brand-border px-3 text-xs text-brand-text-secondary"
            >
              Close
            </button>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead className="border-b border-brand-border uppercase text-brand-text-secondary">
                <tr>
                  <th className="px-3 py-2 font-medium">Revision</th>
                  <th className="px-3 py-2 font-medium">Operator</th>
                  <th className="px-3 py-2 font-medium">Committed</th>
                  <th className="px-3 py-2 font-medium">Environment</th>
                  <th className="px-3 py-2 font-medium">Worker</th>
                  <th className="px-3 py-2 font-medium">Schema</th>
                  <th className="px-3 py-2 font-medium">Segments</th>
                  <th className="px-3 py-2 font-medium">Reasons</th>
                  <th className="px-3 py-2 font-medium">DDB</th>
                </tr>
              </thead>
              <tbody>
                {(submissions[historyEpisode] ?? []).map((submission) => (
                  <tr
                    key={`${submission.episode_id}-${submission.revision}`}
                    className="border-b border-brand-border/70 last:border-0"
                  >
                    <td className="px-3 py-2 font-mono">{submission.revision}</td>
                    <td className="px-3 py-2">{submission.operator}</td>
                    <td className="px-3 py-2">
                      {new Date(submission.completed_at_ms).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 uppercase">{submission.environment}</td>
                    <td
                      className="max-w-40 truncate px-3 py-2 font-mono"
                      title={submission.worker_version}
                    >
                      {submission.worker_version}
                    </td>
                    <td className="px-3 py-2">V{submission.submission_version}</td>
                    <td className="px-3 py-2">{submission.segment_count}</td>
                    <td className="px-3 py-2">{submission.reason_selection_count}</td>
                    <td className="px-3 py-2 capitalize">{submission.ddb_sync_status}</td>
                  </tr>
                ))}
                {(submissions[historyEpisode]?.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-brand-text-secondary">
                      Loading history or no committed revisions found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {diagnostic ? (
        <section
          aria-label={`Source diagnostic for ${diagnostic.episode_id}`}
          className="mt-4 border border-brand-border bg-brand-surface px-4 py-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-brand-text-primary">Source diagnostic</h2>
              <p className="mt-1 font-mono text-xs text-brand-text-secondary">
                {diagnostic.episode_id}
              </p>
            </div>
            <span
              className={
                diagnostic.checks.write_condition_ready ? 'text-emerald-300' : 'text-red-300'
              }
            >
              {diagnostic.checks.write_condition_ready ? 'Write-ready' : 'Source mismatch'}
            </span>
          </div>
          <dl className="mt-4 grid gap-x-6 gap-y-3 text-xs sm:grid-cols-2 lg:grid-cols-3">
            <DiagnosticValue
              label="Audit hash"
              taskValue={diagnostic.task.source_audit_hash}
              ddbValue={diagnostic.ddb.source_audit_hash}
              matches={diagnostic.checks.source_audit_hash_matches}
            />
            <DiagnosticValue
              label="Duration"
              taskValue={diagnostic.task.duration_s}
              ddbValue={diagnostic.ddb.duration_s}
              matches={diagnostic.checks.duration_matches}
            />
            <DiagnosticValue
              label="Annotation FPS"
              taskValue={diagnostic.task.annotation_fps}
              ddbValue={diagnostic.ddb.annotation_fps}
              matches={diagnostic.checks.annotation_fps_matches}
            />
            <DiagnosticValue
              label="Reason revision"
              taskValue={diagnostic.task.last_committed_revision}
              ddbValue={diagnostic.ddb.reason_revision}
              matches={diagnostic.checks.reason_revision_writable}
              matchLabel="writable"
              mismatchLabel="blocked"
            />
            <DiagnosticValue
              label="Source complete"
              taskValue={diagnostic.checks.source_complete}
              ddbValue={diagnostic.ddb.seccap_done}
              matches={diagnostic.checks.source_complete}
            />
            <DiagnosticValue
              label="Frame / bitmap size"
              taskValue={diagnostic.task.num_frames}
              ddbValue={diagnostic.ddb.source_bitmap_length}
            />
          </dl>
        </section>
      ) : null}
      {hasMore && onLoadMore ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            className="h-9 rounded-md border border-brand-border px-4 text-sm text-brand-text-primary disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      ) : null}
    </main>
  );
}

function DiagnosticValue({
  label,
  taskValue,
  ddbValue,
  matches,
  matchLabel = 'match',
  mismatchLabel = 'mismatch',
}: {
  label: string;
  taskValue: string | number | boolean | null;
  ddbValue: string | number | boolean | null;
  matches?: boolean;
  matchLabel?: string;
  mismatchLabel?: string;
}) {
  const display = (value: string | number | boolean | null) =>
    value == null ? 'null' : String(value);
  return (
    <div>
      <dt className="text-brand-text-secondary">
        {label}
        {matches == null ? null : (
          <>
            {' '}
            <span className={matches ? 'text-emerald-300' : 'text-red-300'}>
              {matches ? matchLabel : mismatchLabel}
            </span>
          </>
        )}
      </dt>
      <dd className="mt-1 break-all font-mono text-brand-text-primary">
        Task: {display(taskValue)}
      </dd>
      <dd className="mt-1 break-all font-mono text-brand-text-primary">DDB: {display(ddbValue)}</dd>
    </div>
  );
}
